/**
 * LeafLoader — l'attente de la boutique : une feuille jetée en l'air, dans une
 * poignée d'étoiles.
 *
 * Repris du principe de koro.com, qui fait tournoyer une noix de cajou : voile
 * plein écran, silhouette pleine d'un seul tenant, et surtout un mouvement de
 * LANCER — la forme monte en ralentissant, marque son sommet, puis retombe en
 * accélérant, un tour complet accompli. C'est le changement de rythme entre la
 * montée et la chute qui donne le poids ; une rotation régulière ne fait que
 * tourner. Ici c'est une feuille, pas un fruit sec : on est maraîcher.
 *
 * Les étoiles scintillent chacune à son rythme autour de la trajectoire : elles
 * grandissent ET se révèlent en fondu. Sans le fondu, une étoile a l'air de
 * gonfler plutôt que de scintiller — c'est lui qui fait l'étincelle.
 *
 * Animation CSS seule (aucune image, aucune requête), neutralisée sous
 * prefers-reduced-motion — cf. index.css.
 */

interface LeafLoaderProps {
  /** Taille de la feuille en pixels. */
  size?: number;
  label?: string;
  className?: string;
}

/**
 * Position (en % de la zone), taille, durée et retard de chaque étoile.
 * Écrites à la main plutôt que tirées au hasard : le hasard produit des paquets
 * et des trous, et il change à chaque rendu.
 */
const ETOILES = [
  { left: '18%', top: '20%', size: 13, duree: '1.5s', retard: '0s' },
  { left: '76%', top: '28%', size: 10, duree: '1.9s', retard: '0.35s' },
  { left: '30%', top: '48%', size: 8, duree: '1.4s', retard: '0.7s' },
  { left: '82%', top: '58%', size: 14, duree: '2.1s', retard: '0.2s' },
  { left: '12%', top: '68%', size: 9, duree: '1.7s', retard: '0.95s' },
  { left: '62%', top: '82%', size: 11, duree: '1.6s', retard: '0.5s' },
] as const;

export default function LeafLoader({ size = 56, label, className = '' }: LeafLoaderProps) {
  // La feuille traverse verticalement : on lui réserve la hauteur du lancer,
  // sinon elle serait rognée en haut et en bas de sa course.
  const hauteur = size * 4;
  const largeur = size * 4;

  return (
    <div className={`flex flex-col items-center justify-center ${className}`} role="status" aria-live="polite">
      <div className="relative flex items-center justify-center overflow-hidden" style={{ height: hauteur, width: largeur }}>
        {ETOILES.map((e, i) => (
          <svg
            key={i}
            width={e.size}
            height={e.size}
            viewBox="0 0 24 24"
            className="animate-twinkle absolute"
            style={{ left: e.left, top: e.top, animationDuration: e.duree, animationDelay: e.retard }}
            aria-hidden="true"
          >
            {/* Étoile à quatre branches, aux flancs creusés : c'est ce galbe qui
                la fait lire comme un scintillement et non comme une croix. */}
            <path
              d="M12 0c0 6.6 5.4 12 12 12-6.6 0-12 5.4-12 12 0-6.6-5.4-12-12-12C6.6 12 12 6.6 12 0z"
              fill="#b45f2e"
            />
          </svg>
        ))}

        <svg
          width={size}
          height={size}
          viewBox="0 0 64 64"
          className="animate-toss-spin relative"
          aria-hidden="true"
        >
          {/* Limbe */}
          <path d="M10 54C10 28 28 10 54 10c0 26-18 44-44 44z" fill="#4a5c3a" />
          {/* Pétiole */}
          <path d="M11 53 4.5 59.5" stroke="#4a5c3a" strokeWidth="4.5" strokeLinecap="round" />
        </svg>
      </div>
      {label && <p className="text-[#4a4530] text-sm font-medium">{label}</p>}
      <span className="sr-only">Chargement en cours</span>
    </div>
  );
}
